import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { Factory, Loader2, Mail, Phone, Send } from 'lucide-react';
import { API } from '@/data/catalogue';
import { useCollections } from '@/hooks/useCollections';

const EMPTY = { name: '', email: '', phone: '', project_type: 'Residential', collection: '', message: '' };

export const Contact = () => {
  const collections = useCollections();
  const location = useLocation();
  const appliedRef = useRef(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (appliedRef.current) return;
    const p = location.state?.prefill;
    if (!p) return;
    appliedRef.current = true;
    const { collection, model, product, finish } = p;
    const lines = [];
    if (model || product) lines.push(`Interested in: ${[model, product].filter(Boolean).join(' — ')}`);
    if (finish) lines.push(`Finish preference: ${finish}`);
    lines.push('Please share pricing and availability.');
    setForm((f) => ({ ...f, collection: collection || f.collection, message: lines.join('\n') }));
    toast.info(model ? `${model} added to your enquiry` : `${collection} added to your enquiry`, {
      description: 'Complete the form and we’ll respond with pricing.',
    });
  }, [location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success('Enquiry sent', {
        description: 'Thank you — the Jakson team will respond with the price list shortly.',
      });
      setForm(EMPTY);
    } catch {
      toast.error('Could not send', { description: 'Please try again or email works.hindustanmetaworks@gmail.com.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-enquiry-form" className="py-28 sm:py-36 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 50% 55% at 85% 20%, rgba(212,175,55,0.07), transparent 60%)' }}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="grid lg:grid-cols-5 gap-16">
          <div className="lg:col-span-2">
            <p className="kicker mb-4">Enquiries &amp; Price List</p>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight font-light">
              Let’s talk<br />
              <span className="italic text-gold-gradient">water.</span>
            </h2>
            <p className="mt-6 text-zinc-400 font-light leading-relaxed max-w-sm">
              Tell us about your project — residence, hotel or commercial space — and
              we’ll share the April 2026 price list with collection recommendations.
            </p>

            <div className="mt-10 space-y-5" data-testid="contact-details">
              <a href="tel:+919811860677" data-testid="contact-phone" className="flex items-start gap-4 group">
                <span className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center group-hover:bg-[#D4AF37]/10 transition-colors">
                  <Phone size={16} className="text-[#D4AF37]" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Phone</span>
                  <span className="text-zinc-200 group-hover:text-[#F5E6AD] transition-colors">+91 98118 60677</span>
                </span>
              </a>
              <a href="mailto:works.hindustanmetaworks@gmail.com" data-testid="contact-email" className="flex items-start gap-4 group">
                <span className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center group-hover:bg-[#D4AF37]/10 transition-colors">
                  <Mail size={16} className="text-[#D4AF37]" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Email</span>
                  <span className="text-zinc-200 group-hover:text-[#F5E6AD] transition-colors">works.hindustanmetaworks@gmail.com</span>
                </span>
              </a>
              <div className="flex items-start gap-4" data-testid="contact-factory">
                <span className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                  <Factory size={16} className="text-[#D4AF37]" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Factory</span>
                  <span className="text-zinc-200">Hindustan Meta Works</span>
                  <span className="block text-sm text-zinc-500 mt-1">
                    G1-793A RIICO Industrial Area,<br />Bhiwadi, Rajasthan — 301019
                  </span>
                </span>
              </div>
            </div>
          </div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-3 bg-[#121215] border border-[#D4AF37]/20 p-7 sm:p-10"
            data-testid="enquiry-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                required
                data-testid="enquiry-input-name"
                value={form.name}
                onChange={set('name')}
                placeholder="Your name *"
                className="bg-[#0A0A0B] border border-zinc-800 px-4 py-3.5 text-sm placeholder:text-zinc-600 focus:border-[#D4AF37] focus:outline-none transition-colors"
              />
              <input
                required
                type="email"
                data-testid="enquiry-input-email"
                value={form.email}
                onChange={set('email')}
                placeholder="Email *"
                className="bg-[#0A0A0B] border border-zinc-800 px-4 py-3.5 text-sm placeholder:text-zinc-600 focus:border-[#D4AF37] focus:outline-none transition-colors"
              />
              <input
                data-testid="enquiry-input-phone"
                value={form.phone}
                onChange={set('phone')}
                placeholder="Phone"
                className="bg-[#0A0A0B] border border-zinc-800 px-4 py-3.5 text-sm placeholder:text-zinc-600 focus:border-[#D4AF37] focus:outline-none transition-colors"
              />
              <select
                data-testid="enquiry-input-project-type"
                value={form.project_type}
                onChange={set('project_type')}
                className="bg-[#0A0A0B] border border-zinc-800 px-4 py-3.5 text-sm text-zinc-300 focus:border-[#D4AF37] focus:outline-none transition-colors"
              >
                <option>Residential</option>
                <option>Hospitality</option>
                <option>Commercial</option>
                <option>Retail / Dealer</option>
              </select>
            </div>
            <select
              data-testid="enquiry-input-collection"
              value={form.collection}
              onChange={set('collection')}
              className="mt-4 w-full bg-[#0A0A0B] border border-zinc-800 px-4 py-3.5 text-sm text-zinc-300 focus:border-[#D4AF37] focus:outline-none transition-colors"
            >
              <option value="">Collection of interest (optional)</option>
              {collections.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name} — {c.line}
                </option>
              ))}
            </select>
            <textarea
              data-testid="enquiry-input-message"
              value={form.message}
              onChange={set('message')}
              placeholder="Project details, quantities, finishes you’re considering…"
              rows={5}
              className="mt-4 w-full bg-[#0A0A0B] border border-zinc-800 px-4 py-3.5 text-sm placeholder:text-zinc-600 focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="enquiry-submit-btn"
              className="mt-6 w-full sm:w-auto flex items-center justify-center gap-3 bg-[#D4AF37] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] px-12 py-4 hover:bg-[#F5E6AD] transition-colors duration-300 disabled:opacity-60"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              {busy ? 'Sending…' : 'Send Enquiry'}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
